import Big from 'big.js';
import { Map as YMap } from 'yjs';

import { randomBase32String, reduceMax } from 'js/utils/string';
import {
  calcPositionBetween,
  getOrInitializeMap,
} from 'js/ydoc/docs/common/utils';
import {
  createAnswerSet,
  copyAnswerSet,
  deleteAnswerSetIfUnused,
} from './answer-set-actions';
import {
  getElementPage,
  getFollowingPos,
  getLastPosition,
  moveAfter,
  moveBefore,
} from './utils';

const STANDARD_POSITION_KEY = 'pageElements';

export function addCapabilityElement(yDoc, elementProps = {}, pageGuid) {
  const guid = randomBase32String(10);
  const q = new YMap();
  q.set('guid', guid);
  q.set('elementType', 'question');
  q.set('type', 'likert');
  q.set('text', elementProps.text);
  q.set('name', elementProps.name);
  q.set('key', `cq-${guid}`);

  yDoc.transact(() => {
    if (pageGuid != null) {
      const page = yDoc.getMap('pages').get(pageGuid);
      if (page && page.get('type') !== 'capability-questions') {
        const capabilityElementPositions = page.get(
          'capabilityElementPositions'
        );
        const maxPosition = Object.values(
          capabilityElementPositions.toJSON()
        ).reduce((acc, p) => (p > acc ? p : acc), null);

        const newPosition =
          maxPosition === null
            ? calcPositionBetween(Big(0), Big(1))
            : calcPositionBetween(Big(maxPosition), Big(1));

        capabilityElementPositions.set(guid, newPosition.toString());
      }
    }

    const answerSetGuid = createAnswerSet(yDoc, {});
    q.set('answerSetId', answerSetGuid);

    yDoc.getMap('capabilityElements').set(guid, q);
  });

  return guid;
}

export function addElementToCapabilityPage(yDoc, pageGuid, elementProps = {}) {
  const guid = randomBase32String(10);
  const page = yDoc.getMap('pages').get(pageGuid);

  if (!page) {
    console.warn('Page not found');
    return;
  }

  const q = new YMap();
  q.set('guid', guid);
  q.set('elementType', 'question');
  q.set('key', `q-${guid}`);
  q.set('text', elementProps.text ?? null);
  q.set('type', elementProps.type ?? 'long-text');
  q.set('answerSetId', elementProps.answerSetId ?? null);

  yDoc.getMap('elements').set(guid, q);
  const currentPositions = Object.values(
    getOrInitializeMap(page, 'additionalElementPositions')?.toJSON() ?? {}
  );

  const maxPosition = currentPositions.reduce(reduceMax, null);

  const newPosition =
    maxPosition === null
      ? calcPositionBetween(Big(0), Big(1))
      : calcPositionBetween(Big(maxPosition), Big(1));

  page.get('additionalElementPositions').set(guid, newPosition.toString());
}

export function copyAdditionalElement(yDoc, pageGuid, elementGuid) {
  const page = yDoc.getMap('pages').get(pageGuid);
  const element = yDoc.getMap('elements').get(elementGuid);

  if (!page || !element) {
    console.warn('Page or element not found');
    return;
  }

  const newElementGuid = randomBase32String(10);
  const newElement = element.clone();
  newElement.set('guid', newElementGuid);
  newElement.set('key', `q-${newElementGuid}`);

  const maxPosition = Object.values(
    page.get('additionalElementPositions').toJSON()
  ).reduce(reduceMax, '0');

  const newPosition = calcPositionBetween(Big(maxPosition), Big(1));

  yDoc.transact(() => {
    copyAnswerSetFor(yDoc, element, newElement);
    yDoc.getMap('elements').set(newElementGuid, newElement);
    page
      .get('additionalElementPositions')
      .set(newElementGuid, newPosition.toString());
  });
}

function standardPosition(pageGuid, p) {
  const position = new YMap();
  position.set(
    STANDARD_POSITION_KEY,
    new YMap([
      ['t', pageGuid],
      ['p', p],
    ])
  );
  return position;
}

function nextStandardPosition(yDoc, pageGuid) {
  const lastPosition =
    getLastPosition(yDoc, 'elements', STANDARD_POSITION_KEY, pageGuid) ?? '0';
  return calcPositionBetween(Big(lastPosition), Big(1)).toString();
}

/** Copies a question's answer set unless it's a published, shared one. */
function copyAnswerSetFor(yDoc, element, newElement) {
  const answerSetGuid = element.get('answerSetId');
  const isPublishedAnswerSet =
    true ===
    (answerSetGuid &&
      yDoc.getMap('answerSets').get(answerSetGuid)?.get('isPublished'));

  if (answerSetGuid && !isPublishedAnswerSet) {
    newElement.set('answerSetId', copyAnswerSet(yDoc, answerSetGuid));
  }
}

export function addElementToStandardPage(yDoc, pageGuid, elementProps = {}) {
  const page = yDoc.getMap('pages').get(pageGuid);

  if (!page) {
    console.warn('Page not found');
    return;
  }

  const guid = randomBase32String(10);
  const q = new YMap();
  q.set('guid', guid);
  q.set('elementType', 'question');
  q.set('key', `q-${guid}`);
  q.set('text', elementProps.text ?? '');
  q.set('type', elementProps.type ?? 'likert');
  q.set('answerSetId', elementProps.answerSetId ?? null);
  q.set(
    'position',
    standardPosition(pageGuid, nextStandardPosition(yDoc, pageGuid))
  );

  yDoc.getMap('elements').set(guid, q);

  return guid;
}

export function copyStandardElement(yDoc, elementGuid) {
  const element = yDoc.getMap('elements').get(elementGuid);
  const pageGuid = getElementPage(element);

  if (!element || !pageGuid) {
    console.warn('Standard question not found');
    return;
  }

  const p = element.get('position').get(STANDARD_POSITION_KEY).get('p');
  const followingPosition = getFollowingPos(
    yDoc,
    p,
    'elements',
    STANDARD_POSITION_KEY,
    pageGuid
  );

  const newElementGuid = randomBase32String(10);
  const newElement = element.clone();
  newElement.set('guid', newElementGuid);
  newElement.set('key', `q-${newElementGuid}`);
  newElement.set(
    'position',
    standardPosition(
      pageGuid,
      calcPositionBetween(Big(p), Big(followingPosition ?? 1)).toString()
    )
  );

  yDoc.transact(() => {
    copyAnswerSetFor(yDoc, element, newElement);
    yDoc.getMap('elements').set(newElementGuid, newElement);
  });

  return newElementGuid;
}

export function deleteStandardElement(yDoc, elementGuid) {
  const elements = yDoc.getMap('elements');
  const answerSetGuid = elements.get(elementGuid)?.get('answerSetId');

  yDoc.transact(() => {
    elements.delete(elementGuid);
    deleteAnswerSetIfUnused(yDoc, answerSetGuid);
  });
}

/** True when both questions are on standard pages, the only place they move. */
function areStandardElements(yDoc, ...elementGuids) {
  const elements = yDoc.getMap('elements');
  return elementGuids.every((guid) => !!getElementPage(elements.get(guid)));
}

export function moveStandardElementBefore(
  yDoc,
  elementGuid,
  beforeElementGuid
) {
  if (
    elementGuid === beforeElementGuid ||
    !areStandardElements(yDoc, elementGuid, beforeElementGuid)
  ) {
    return;
  }

  moveBefore(
    yDoc,
    'elements',
    STANDARD_POSITION_KEY,
    elementGuid,
    beforeElementGuid
  );
}

export function moveStandardElementAfter(yDoc, elementGuid, afterElementGuid) {
  if (
    elementGuid === afterElementGuid ||
    !areStandardElements(yDoc, elementGuid, afterElementGuid)
  ) {
    return;
  }

  moveAfter(
    yDoc,
    'elements',
    STANDARD_POSITION_KEY,
    elementGuid,
    afterElementGuid
  );
}

/** Moves a standard question to the end of another standard page. */
export function moveStandardElementToPage(yDoc, elementGuid, pageGuid) {
  const element = yDoc.getMap('elements').get(elementGuid);
  const page = yDoc.getMap('pages').get(pageGuid);

  if (
    !getElementPage(element) ||
    page?.get('type') !== 'standard-questions' ||
    getElementPage(element) === pageGuid
  ) {
    return;
  }

  const position = element.get('position').get(STANDARD_POSITION_KEY);
  const p = nextStandardPosition(yDoc, pageGuid);

  yDoc.transact(() => {
    position.set('t', pageGuid);
    position.set('p', p);
  });
}

// export function addQuestionToSet(doc, questionSetId, type, initialText) {
//   const guid = randomBase32String(10);
//   const q = new YMap();
//   q.set('elementType', 'question');
//   q.set('text', initialText);
//   q.set('guid', guid);
//   q.set('key', `q-${guid}`);
//   q.set('type', type);

//   const position = buildPositionMap(
//     doc.getMap('elements'),
//     'pageElements',
//     questionSetId
//   );

//   q.set('position', position);
//   doc.getMap('elements').set(guid, q);

//   return guid;
// }

export function updateElement(doc, elementGuid, updates) {
  const elements = doc.getMap('elements');
  const element = elements.get(elementGuid);
  if (!element) {
    return;
  }

  doc.transact(() => {
    Object.entries(updates).forEach(([key, value]) => {
      element.set(key, value);
    });
  });
}

export function removeElementFromCapabilityPage(doc, pageGuid, elementGuid) {
  const page = doc.getMap('pages').get(pageGuid);
  if (!page) {
    return;
  }

  // TODO: decide if we want to delete the element entirely
  page.get('additionalElementPositions').delete(elementGuid);
}
